import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FaDownload, FaTrash, FaDatabase, FaPlus, FaClock, FaFileAlt } from 'react-icons/fa';
import { toast } from 'react-toastify';
import API_BASE_URL from '../../components/apiconfig/api-config';
import NoRecordFound from '../../components/common/no-record-found';

const BackupManagement = () => {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creatingBackup, setCreatingBackup] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);
    return () => clearTimeout(handler);
  }, [search]);

  // Fetch backups list
  const fetchBackups = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/backup/list-backups`, {
        withCredentials: true,
      });
      
      if (response.data.success) {
        let filteredBackups = response.data.backups;
        if (debouncedSearch) {
          filteredBackups = filteredBackups.filter(backup => 
            backup.filename.toLowerCase().includes(debouncedSearch.toLowerCase())
          );
        }
        setBackups(filteredBackups);
      }
    } catch (error) {
      console.error('Error fetching backups:', error);
      toast.error('Failed to fetch backup list');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch]);

  // Create new backup
  const createBackup = async () => {
    try {
      setCreatingBackup(true);
      const response = await axios.post(`${API_BASE_URL}/api/backup/create-backup`, {}, {
        withCredentials: true,
      });
      
      if (response.data.success) {
        toast.success('Database backup created successfully!');
        fetchBackups(); // Refresh the list
      }
    } catch (error) {
      console.error('Error creating backup:', error);
      toast.error('Failed to create backup. Please try again.');
    } finally {
      setCreatingBackup(false);
    }
  };

  // Download backup file
  const downloadBackup = async (filename) => {
    try {
      setDownloading(filename);
      const response = await axios.get(`${API_BASE_URL}/api/backup/download/${filename}`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Backup downloaded successfully!');
    } catch (error) {
      console.error('Error downloading backup:', error);
      toast.error('Failed to download backup file');
    } finally {
      setDownloading(null);
    }
  };

  // Delete backup file
  const deleteBackup = async (filename) => {
    if (!window.confirm('Are you sure you want to delete this backup file?')) {
      return;
    }
    
    try {
      const response = await axios.delete(`${API_BASE_URL}/api/backup/delete/${filename}`, {
        withCredentials: true,
      });
      
      if (response.data.success) {
        toast.success('Backup file deleted successfully!');
        fetchBackups(); // Refresh the list
      }
    } catch (error) {
      console.error('Error deleting backup:', error);
      toast.error('Failed to delete backup file');
    }
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Load backups on component mount and when search changes
  useEffect(() => {
    fetchBackups();
  }, [fetchBackups]);

  return (
    <div className="max-w-8xl mx-auto p-3 bg-white rounded-md shadow-sm border border-gray-300">
      <div className="flex flex-col sm:flex-row justify-between items-center pb-2 mb-3 border-b border-gray-200 gap-2">
        <div>
          <h2 className="text-[15px] font-semibold text-gray-900 tracking-tight flex items-center gap-2">
            <FaDatabase className="text-[#15803d]" />
            Database Backup Management
          </h2>
        </div>
        <div className="flex gap-1 w-full sm:w-auto justify-end">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
              <svg className="h-3.5 w-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search backups..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 pr-2 py-1.5 border border-gray-300 rounded-sm focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-colors text-[12px] w-full sm:w-56 outline-none text-gray-900 placeholder-gray-500"
            />
          </div>
          <button
            className="flex items-center justify-center gap-1.5 bg-[#15803d] text-white px-3 py-1.5 text-[12px] font-semibold rounded-sm hover:bg-[#166534] transition-colors shadow-sm active:translate-y-[1px]"
            onClick={createBackup}
            disabled={creatingBackup}
          >
            {creatingBackup ? (
              <>
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
                Creating...
              </>
            ) : (
              <>
                <FaPlus className="text-[10px]" />
                Create Backup
              </>
            )}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center text-[#15803d] py-6 text-[14px]">Loading backups...</div>
      ) : backups.length === 0 ? (
        <NoRecordFound message="No backup files found." />
      ) : (
        <>
          <div className="overflow-x-auto border border-gray-300 rounded-sm">
            <table className="min-w-full text-left border-collapse">
              <thead className="bg-[#f4f4f4] border-b border-gray-300">
                <tr>
                  <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0 whitespace-nowrap">File Name</th>
                  <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0 whitespace-nowrap">Size</th>
                  <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0 whitespace-nowrap">Created Date</th>
                  <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0 whitespace-nowrap text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {backups.map((backup, idx) => (
                  <tr key={backup.filename} className="hover:bg-blue-50/50 transition-colors duration-150 border-b border-gray-200 last:border-b-0">
                    <td className="px-3 py-1.5 text-gray-800 text-[12px] border-r border-gray-100 last:border-r-0 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <FaFileAlt className="text-[#15803d]" />
                        {backup.filename}
                      </div>
                    </td>
                    <td className="px-3 py-1.5 text-gray-800 text-[12px] border-r border-gray-100 last:border-r-0 whitespace-nowrap">{backup.size_formatted}</td>
                    <td className="px-3 py-1.5 text-gray-800 text-[12px] border-r border-gray-100 last:border-r-0 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <FaClock className="text-gray-400" />
                        {formatDate(backup.created_at)}
                      </div>
                    </td>
                    <td className="px-3 py-1.5 text-gray-800 text-[12px] border-r border-gray-100 last:border-r-0 whitespace-nowrap text-center">
                      <div className="flex justify-center gap-1.5">
                        <button
                          onClick={() => downloadBackup(backup.filename)}
                          disabled={downloading === backup.filename}
                          className="text-[12px] p-1.5 bg-gray-50 text-gray-600 rounded-sm hover:bg-green-50 hover:text-green-700 transition-colors border border-gray-200 hover:border-green-200 inline-block disabled:opacity-50"
                          title="Download Backup"
                        >
                          {downloading === backup.filename ? (
                            <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-gray-600"></div>
                          ) : (
                            <FaDownload className="text-[14px]" />
                          )}
                        </button>
                        <button
                          onClick={() => deleteBackup(backup.filename)}
                          className="text-[12px] p-1.5 bg-gray-50 text-gray-600 rounded-sm hover:bg-red-50 hover:text-red-700 transition-colors border border-gray-200 hover:border-red-200 inline-block"
                          title="Delete Backup"
                        >
                          <FaTrash className="text-[14px]" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-3">
              <FaDatabase className="text-blue-400 mt-1 flex-shrink-0" />
              <div>
                <h3 className="text-[13px] font-medium text-[#15803d] mb-2">Backup Information</h3>
                <ul className="text-[13px] text-[#15803d] space-y-1">
                  <li>• Backups are stored in SQL format and contain your complete database</li>
                  <li>• Each backup includes the date and time in the filename</li>
                  <li>• You can download backups to your local system for safekeeping</li>
                  <li>• Regular backups are recommended for data protection</li>
                  <li>• Automatic backups run daily at 6:00 PM</li>
                </ul>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default BackupManagement;   